/*
 * ww-product-tabs.js — WaveWear 상품 탭 캐러셀
 * sections/ww-product-tabs.liquid 전용. 다른 섹션과 공유하지 않습니다.
 *
 * 하는 일 세 가지
 *  1) 탭 전환 (패널의 hidden 속성만 토글, 키보드 화살표 지원)
 *  2) 캐러셀 좌우 화살표 (스크롤 자체는 CSS scroll-snap 이 담당)
 *  3) 카드 영상 재생 (hover / autoplay). 재생 직전에야 파일을 내려받습니다.
 *
 * Dawn 16 이 assets/global.js 에서 쓰는 것과 같은 커스텀 엘리먼트 방식입니다.
 */
if (!customElements.get('ww-tabs')) {
  class WwTabs extends HTMLElement {
    connectedCallback() {
      if (this.wwReady) return;
      this.wwReady = true;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.noHover = window.matchMedia('(hover: none)');

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, false));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.panels.forEach((panel) => this.setupPanel(panel));
      this.setupVideos();
    }

    select(index, moveFocus) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && moveFocus) tab.focus();
      });

      this.panels.forEach((panel, i) => {
        const active = i === index;
        panel.hidden = !active;
        if (active) this.updateArrows(panel);
        else this.pausePanelVideos(panel);
      });
    }

    onKeydown(event, index) {
      const last = this.tabs.length - 1;
      let target = null;

      if (event.key === 'ArrowRight') target = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft') target = index === 0 ? last : index - 1;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = last;
      else return;

      event.preventDefault();
      this.select(target, true);
    }

    setupPanel(panel) {
      const track = panel.querySelector('.ww-tabs__track');
      const prev = panel.querySelector('.ww-tabs__arrow--prev');
      const next = panel.querySelector('.ww-tabs__arrow--next');
      if (!track || !prev || !next) return;

      let ticking = false;
      const update = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          this.updateArrows(panel);
        });
      };

      prev.addEventListener('click', () => {
        track.scrollBy({ left: -this.stepFor(track), behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        track.scrollBy({ left: this.stepFor(track), behavior: 'smooth' });
      });

      track.addEventListener('scroll', update, { passive: true });

      if (window.ResizeObserver) new ResizeObserver(update).observe(track);
      else window.addEventListener('resize', update);

      this.updateArrows(panel);
    }

    stepFor(track) {
      const slides = track.querySelectorAll('.ww-tabs__slide');
      if (!slides.length) return Math.round(track.clientWidth * 0.9);

      // 슬라이드 사이 간격(gap)까지 포함한 실제 이동 거리
      const stride =
        slides.length > 1
          ? slides[1].offsetLeft - slides[0].offsetLeft
          : slides[0].getBoundingClientRect().width;

      if (!stride) return Math.round(track.clientWidth * 0.9);

      const perView = Math.max(1, Math.round(track.clientWidth / stride));
      return Math.round(stride * perView);
    }

    updateArrows(panel) {
      const track = panel.querySelector('.ww-tabs__track');
      const prev = panel.querySelector('.ww-tabs__arrow--prev');
      const next = panel.querySelector('.ww-tabs__arrow--next');
      if (!track || !prev || !next) return;

      const max = track.scrollWidth - track.clientWidth;
      const overflows = max > 4;

      prev.hidden = !overflows;
      next.hidden = !overflows;
      if (!overflows) return;

      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    }

    setupVideos() {
      if (this.reducedMotion.matches) return;

      const holders = Array.from(this.querySelectorAll('[data-ww-video]'));
      if (!holders.length) return;

      let observer = null;
      if (window.IntersectionObserver) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const holder = entry.target;
              const video = holder.querySelector('video');
              if (!video) return;
              if (entry.isIntersecting) this.playVideo(holder, video);
              else this.stopVideo(holder, video);
            });
          },
          { threshold: 0.4 }
        );
      }

      holders.forEach((holder) => {
        const video = holder.querySelector('video');
        if (!video) return;

        const hoverMode = holder.getAttribute('data-ww-video') === 'hover' && !this.noHover.matches;

        if (hoverMode) {
          // 이 스토어에는 페이지의 모든 <video> 에 autoplay 를 강제로 붙이는 외부
          // 앱 스크립트가 있습니다(inline enableVideoAutoplay, data-autoplay-set).
          // hover 모드에서는 그 속성을 떼어내야 의도대로 동작합니다.
          video.removeAttribute('autoplay');
          video.autoplay = false;

          const card = holder.closest('.ww-card') || holder;
          card.addEventListener('pointerenter', () => this.playVideo(holder, video));
          card.addEventListener('pointerleave', () => this.stopVideo(holder, video));
          card.addEventListener('focusin', () => this.playVideo(holder, video));
          card.addEventListener('focusout', () => this.stopVideo(holder, video));
        } else if (observer) {
          observer.observe(holder);
        }
      });
    }

    playVideo(holder, video) {
      const source = video.querySelector('source[data-ww-src]');
      if (source && !source.getAttribute('src')) {
        source.setAttribute('src', source.getAttribute('data-ww-src'));
        video.load();
      }
      video.muted = true;
      const attempt = video.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt
          .then(() => holder.classList.add('is-playing'))
          .catch(() => holder.classList.remove('is-playing'));
      } else {
        holder.classList.add('is-playing');
      }
    }

    stopVideo(holder, video) {
      holder.classList.remove('is-playing');
      if (holder.getAttribute('data-ww-video') === 'hover') {
        video.removeAttribute('autoplay');
        video.autoplay = false;
      }
      try {
        video.pause();
      } catch (error) {
        /* 재생이 시작되기 전에 pause 가 불릴 수 있습니다. 무시합니다. */
      }
    }

    pausePanelVideos(panel) {
      Array.from(panel.querySelectorAll('[data-ww-video]')).forEach((holder) => {
        const video = holder.querySelector('video');
        if (video) this.stopVideo(holder, video);
      });
    }
  }

  customElements.define('ww-tabs', WwTabs);
}
